<script lang="ts">
  import type { Settlement } from "../../lib/types";
  import { settlementApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import { router } from "@inertiajs/svelte";
  import { formatCurrency } from "../../lib/utils";

  let {
    tripId,
    settlement = $bindable(null),
  }: {
    tripId: string;
    settlement?: Settlement | null;
  } = $props();

  let loading = $state(false);
  let completing = $state(false);

  async function calculateSettlement() {
    loading = true;
    try {
      const response = await settlementApi.calculate(tripId);
      settlement = response.data;
    } catch (error) {
      addNotification("error", "정산 계산에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  async function completeSettlement() {
    if (!confirm("모든 지출을 정산 완료 처리하시겠습니까?")) return;

    completing = true;
    try {
      await settlementApi.complete(tripId);
      addNotification("success", "정산이 완료되었습니다.");
      router.reload();
    } catch (error) {
      addNotification("error", "정산 완료 처리에 실패했습니다.");
    } finally {
      completing = false;
    }
  }

  function getBalanceColor(balance: number): string {
    if (balance > 0) return "settlement-summary__balance settlement-summary__balance--positive";
    if (balance < 0) return "settlement-summary__balance settlement-summary__balance--negative";
    return "settlement-summary__balance settlement-summary__balance--neutral";
  }

  function getBalanceText(balance: number): string {
    if (balance > 0) return `+₩${formatCurrency(balance)} 받을 예정`;
    if (balance < 0) return `-₩${formatCurrency(Math.abs(balance))} 보낼 예정`;
    return "정산 완료";
  }
</script>

<div class="settlement-summary rounded-xl p-4">
  <div class="flex items-center justify-between mb-4">
    <h3 class="text-lg font-semibold settlement-summary__title flex items-center gap-2">
      <svg class="w-5 h-5 settlement-summary__accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
      정산 현황
    </h3>
    <div class="flex gap-2">
      <button
        onclick={calculateSettlement}
        disabled={loading}
        class="px-3 py-1.5 text-sm settlement-summary__secondary-button rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? "계산 중..." : "다시 계산"}
      </button>
    </div>
  </div>

  {#if settlement}
    <!-- Summary Stats -->
    <div class="grid grid-cols-2 gap-4 mb-4">
      <div class="settlement-summary__panel rounded-lg p-3">
        <p class="text-xs settlement-summary__meta">총 지출</p>
        <p class="text-lg font-bold settlement-summary__title">₩{formatCurrency(settlement.totalExpenses)}</p>
      </div>
      <div class="settlement-summary__panel rounded-lg p-3">
        <p class="text-xs settlement-summary__meta">1인당 부담금</p>
        <p class="text-lg font-bold settlement-summary__title">₩{formatCurrency(settlement.perPersonShare)}</p>
      </div>
    </div>

    <!-- Participant Balances -->
    <div class="space-y-2 mb-4">
      <h4 class="text-sm font-medium settlement-summary__subheading">참여자별 현황</h4>
      {#each settlement.participants as participant (participant.user.id)}
        <div class="flex items-center justify-between p-3 settlement-summary__panel rounded-lg">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
              {#if participant.user.avatarUrl}
                <img src={participant.user.avatarUrl} alt={participant.user.name} class="w-full h-full rounded-full object-cover" />
              {:else}
                {participant.user.name.charAt(0)}
              {/if}
            </div>
            <div>
              <p class="font-medium settlement-summary__title text-sm">{participant.user.name}</p>
              <p class="text-xs settlement-summary__meta">
                지불: ₩{formatCurrency(participant.paid)} / 부담: ₩{formatCurrency(participant.owed)}
              </p>
            </div>
          </div>
          <span class="text-sm font-medium {getBalanceColor(participant.balance)}">
            {getBalanceText(participant.balance)}
          </span>
        </div>
      {/each}
    </div>

    <!-- Settlement Transactions -->
    {#if settlement.transactions && settlement.transactions.length > 0}
      <div class="settlement-summary__divider pt-4">
        <h4 class="text-sm font-medium settlement-summary__subheading mb-3">정산 방법</h4>
        <div class="space-y-2">
          {#each settlement.transactions as tx}
            <div class="flex items-center justify-between p-3 settlement-summary__transaction rounded-lg">
              <div class="flex items-center gap-2">
                <span class="font-medium settlement-summary__title">{tx.from.name}</span>
                <svg class="w-4 h-4 settlement-summary__meta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                <span class="font-medium settlement-summary__title">{tx.to.name}</span>
              </div>
              <span class="font-bold settlement-summary__accent">₩{formatCurrency(tx.amount)}</span>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Complete Button -->
    <div class="mt-4 pt-4 settlement-summary__divider">
      <button
        onclick={completeSettlement}
        disabled={completing}
        class="w-full py-3 settlement-summary__primary-button rounded-xl font-medium transition-colors disabled:opacity-50"
      >
        {#if completing}처리 중...{:else}<svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> 정산 완료 처리{/if}
      </button>
    </div>
  {:else}
    <div class="text-center py-8">
      <p class="settlement-summary__meta mb-4">정산 정보를 불러오려면 계산 버튼을 클릭하세요.</p>
      <button
        onclick={calculateSettlement}
        disabled={loading}
        class="px-6 py-2 settlement-summary__primary-button rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? "계산 중..." : "정산 계산하기"}
      </button>
    </div>
  {/if}
</div>

<style>
  .settlement-summary {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  }

  .settlement-summary__title {
    color: var(--text-primary);
  }

  .settlement-summary__meta,
  .settlement-summary__subheading {
    color: var(--text-secondary);
  }

  .settlement-summary__accent {
    color: var(--accent);
  }

  .settlement-summary__panel {
    background: color-mix(in srgb, var(--surface-hover) 92%, transparent);
    border: 1px solid color-mix(in srgb, var(--border) 90%, transparent);
  }

  .settlement-summary__transaction {
    background: color-mix(in srgb, var(--accent-dim) 72%, var(--surface-hover));
    border: 1px solid color-mix(in srgb, var(--accent) 16%, var(--border));
  }

  .settlement-summary__divider {
    border-top: 1px solid var(--border);
  }

  .settlement-summary__secondary-button {
    background: color-mix(in srgb, var(--surface-hover) 92%, transparent);
    color: var(--text-secondary);
    border: 1px solid var(--border);
  }

  .settlement-summary__secondary-button:hover {
    background: color-mix(in srgb, var(--accent-dim) 55%, var(--surface-hover));
    color: var(--text-primary);
    border-color: color-mix(in srgb, var(--accent) 28%, var(--border));
  }

  .settlement-summary__primary-button {
    background: var(--accent);
    color: #fff;
  }

  .settlement-summary__primary-button:hover {
    background: var(--accent-hover);
  }

  .settlement-summary__balance--positive {
    color: #15803d;
  }

  .settlement-summary__balance--negative {
    color: #dc2626;
  }

  .settlement-summary__balance--neutral {
    color: var(--text-secondary);
  }

  @media (prefers-color-scheme: dark) {
    .settlement-summary {
      box-shadow: 0 16px 36px rgba(0, 0, 0, 0.28);
    }

    .settlement-summary__balance--positive {
      color: #4ade80;
    }

    .settlement-summary__balance--negative {
      color: #f87171;
    }
  }
</style>
